import { useCallback, useEffect, useState } from "react";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { UsersThree, Plus, EnvelopeSimple, Phone, Wrench, PaperPlaneTilt, UserMinus, UserPlus, Copy, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import SupplierTimesheetPanel from "./SupplierTimesheetPanel";

const EMPTY = { name: "", email: "", phone: "", trade: "" };

/* Supplier portal → Team tab. The supplier manages its own field staff (login type "Supplier operative"). */
export default function SupplierTeamPanel() {
  const [team, setTeam] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/supplier-portal/team");
      setTeam(data.team || []);
      setSupplier(data.supplier || "");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't load your team"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    setSaving(true);
    try {
      const { data } = await API.post("/supplier-portal/team", form);
      setCreated(data);
      setForm(EMPTY);
      toast.success(data.email_delivered ? `Welcome email sent to ${data.email}` : "Team member added — share the temporary password below");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add team member"); }
    finally { setSaving(false); }
  };

  const resend = async (m) => {
    try {
      const { data } = await API.post(`/supplier-portal/team/${m.id}/resend-welcome`);
      setCreated({ ...m, ...data });
      toast.success(data.email_delivered ? `New temporary password emailed to ${m.email}` : "New temporary password generated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't resend"); }
  };

  const toggleActive = async (m) => {
    const off = m.active !== false;
    if (off && !window.confirm(`Deactivate ${m.name}? They'll be signed out and removed from any open jobs.`)) return;
    try {
      if (off) await API.delete(`/supplier-portal/team/${m.id}`);
      else await API.put(`/supplier-portal/team/${m.id}`, { active: true });
      toast.success(off ? `${m.name} deactivated` : `${m.name} reactivated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

  return (
    <div className="space-y-4" data-testid="supplier-team-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><UsersThree size={20} className="text-primary" /> My Team</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Add your engineers and assign them to your jobs. They get their own login to the field portal — directions, arrive on site, before/after photos and job sheets — and only ever see the jobs you give them.</p>
        </div>
        <Button onClick={() => { setCreated(null); setDlg(true); }} data-testid="supplier-team-add-btn"><Plus size={16} className="mr-1" /> Add team member</Button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : team.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center" data-testid="supplier-team-empty">
          <UsersThree size={32} className="mx-auto text-muted-foreground/60 mb-2" />
          <p className="text-sm font-medium">No team members yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add your first engineer to start assigning jobs. You can also assign jobs to yourself and use the field view.</p>
        </div>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2" data-testid="supplier-team-list">
          {team.map((m) => (
            <li key={m.id} className={`rounded-lg border p-3 ${m.active === false ? "border-border bg-muted/30 opacity-70" : "border-border bg-card"}`} data-testid={`supplier-team-member-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{m.name}</p>
                  <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1 truncate"><EnvelopeSimple size={11} /> {m.email}</p>
                  {m.phone && <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1 ml-2"><Phone size={11} /> {m.phone}</p>}
                  {m.trade && <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5"><Wrench size={11} /> {m.trade}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {m.active === false ? <Badge variant="secondary" className="text-[9px]">Inactive</Badge>
                    : m.pending_first_login ? <Badge className="text-[9px] bg-amber-500/15 text-amber-700 hover:bg-amber-500/15" data-testid={`supplier-team-pending-${m.id}`}>Awaiting first login</Badge>
                    : <Badge className="text-[9px] bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">Active</Badge>}
                  <span className="text-[10px] text-muted-foreground" data-testid={`supplier-team-open-jobs-${m.id}`}>{m.open_jobs} open job{m.open_jobs === 1 ? "" : "s"}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 mt-2">
                {m.active !== false && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => resend(m)} data-testid={`supplier-team-resend-${m.id}`}><PaperPlaneTilt size={12} className="mr-1" /> Resend login</Button>}
                <Button size="sm" variant="ghost" className={`h-7 text-xs ${m.active === false ? "text-emerald-700" : "text-destructive"}`} onClick={() => toggleActive(m)} data-testid={`supplier-team-toggle-${m.id}`}>
                  {m.active === false ? <><UserPlus size={12} className="mr-1" /> Reactivate</> : <><UserMinus size={12} className="mr-1" /> Deactivate</>}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="border-t border-border pt-5 mt-6">
        <SupplierTimesheetPanel endpoint="/supplier-portal/team/timesheet" jobLinkBase="/supplier-portal" />
      </div>

      <Dialog open={dlg} onOpenChange={(o) => { setDlg(o); if (!o) setCreated(null); }}>
        <DialogContent data-testid="supplier-team-dialog">
          <DialogHeader><DialogTitle>{created ? "Team member added" : `Add to ${supplier || "my"} team`}</DialogTitle></DialogHeader>
          {created ? (
            <div className="space-y-3" data-testid="supplier-team-created">
              <div className={`rounded-md border p-3 text-xs ${created.email_delivered ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30" : "border-amber-300 bg-amber-50 dark:bg-amber-950/30"}`}>
                {created.email_delivered
                  ? <p className="inline-flex items-center gap-1.5 text-emerald-800 dark:text-emerald-200"><CheckCircle size={14} weight="fill" /> Welcome email with login details sent to <strong>{created.email}</strong>.</p>
                  : <p className="inline-flex items-center gap-1.5 text-amber-800 dark:text-amber-200"><Warning size={14} weight="fill" /> Email couldn't be sent{created.email_error ? ` (${created.email_error})` : ""} — share these details with {created.name} yourself.</p>}
              </div>
              <div className="rounded-md border border-border p-3 space-y-1.5 text-sm">
                <div className="flex items-center justify-between gap-2"><span className="text-muted-foreground text-xs">Login email</span><span className="font-mono text-xs">{created.email}</span></div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs">Temporary password</span>
                  <span className="inline-flex items-center gap-1 font-mono text-xs" data-testid="supplier-team-temp-password">{created.temp_password}<button type="button" onClick={() => copy(created.temp_password)} className="text-muted-foreground hover:text-foreground"><Copy size={12} /></button></span>
                </div>
                <p className="text-[11px] text-muted-foreground">They'll be asked to choose their own password on first login. Sign in at {window.location.origin}/login</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreated(null)} data-testid="supplier-team-add-another">Add another</Button>
                <Button onClick={() => setDlg(false)} data-testid="supplier-team-done">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label className="text-xs">Full name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sam Sparks" data-testid="supplier-team-name" /></div>
              <div><Label className="text-xs">Email * <span className="text-muted-foreground">(their login)</span></Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sam@yourcompany.co.uk" data-testid="supplier-team-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Mobile</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07700 900000" data-testid="supplier-team-phone" /></div>
                <div><Label className="text-xs">Trade</Label><Input value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} placeholder="Electrician" data-testid="supplier-team-trade" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDlg(false)}>Cancel</Button>
                <Button onClick={add} disabled={saving} data-testid="supplier-team-submit">{saving ? "Adding…" : "Add & send login"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
